/**
 * Background removal tool
 * V1: Client-side using Canvas 2D — simple chroma/luminance-based masking.
 * V2 (future): Workers AI or remove.bg API for higher quality.
 *
 * User flow:
 * 1. Click "Remove Background" — runs auto-detection
 * 2. User adjusts threshold slider to refine the mask
 * 3. Click "Apply" to replace the artwork image with the result
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { Button, Slider, cn } from '@frameit/ui';
import { useSelectedArtwork, getLocalImageUrl } from '../../stores/projectStore';

/** Background detection mode */
type DetectionMode = 'auto' | 'light' | 'dark' | 'color';

/** Remove background from image data using flood-fill edge detection */
function removeBackground(
  imageData: ImageData,
  threshold: number,
  mode: DetectionMode,
  targetColor?: { r: number; g: number; b: number },
): ImageData {
  const { data, width, height } = imageData;
  const output = new Uint8ClampedArray(data);

  // Determine background color from corners (sampling 5x5 from each corner)
  const sampleCorners = (): { r: number; g: number; b: number } => {
    let rSum = 0,
      gSum = 0,
      bSum = 0,
      count = 0;
    const sampleSize = 5;
    const corners = [
      [0, 0],
      [width - sampleSize, 0],
      [0, height - sampleSize],
      [width - sampleSize, height - sampleSize],
    ];

    for (const [cx, cy] of corners) {
      for (let dy = 0; dy < sampleSize; dy++) {
        for (let dx = 0; dx < sampleSize; dx++) {
          const idx = ((cy + dy) * width + (cx + dx)) * 4;
          rSum += data[idx];
          gSum += data[idx + 1];
          bSum += data[idx + 2];
          count++;
        }
      }
    }

    return {
      r: Math.round(rSum / count),
      g: Math.round(gSum / count),
      b: Math.round(bSum / count),
    };
  };

  const bgColor = targetColor ?? sampleCorners();
  const scaledThreshold = threshold * 2.55; // 0-100 → 0-255

  // For each pixel, check if it's close to the background color
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];

    let isBackground = false;

    switch (mode) {
      case 'light':
        // Remove light backgrounds
        isBackground = (r + g + b) / 3 > 255 - scaledThreshold;
        break;
      case 'dark':
        // Remove dark backgrounds
        isBackground = (r + g + b) / 3 < scaledThreshold;
        break;
      case 'color':
      case 'auto':
      default: {
        // Color distance from background
        const dist = Math.sqrt((r - bgColor.r) ** 2 + (g - bgColor.g) ** 2 + (b - bgColor.b) ** 2);
        isBackground = dist < scaledThreshold;
        break;
      }
    }

    if (isBackground) {
      // Make transparent with soft edge
      const softness = 0.3;
      let dist: number;

      if (mode === 'light') {
        dist = ((r + g + b) / 3 - (255 - scaledThreshold)) / (scaledThreshold * softness);
      } else if (mode === 'dark') {
        dist = (scaledThreshold - (r + g + b) / 3) / (scaledThreshold * softness);
      } else {
        dist =
          (scaledThreshold -
            Math.sqrt((r - bgColor.r) ** 2 + (g - bgColor.g) ** 2 + (b - bgColor.b) ** 2)) /
          (scaledThreshold * softness);
      }

      const alpha = Math.max(0, Math.min(255, Math.round((1 - Math.max(0, dist)) * 255)));
      output[i + 3] = alpha;
    }
  }

  // Flood-fill from edges to ensure only outer background is removed
  const visited = new Uint8Array(width * height);
  const queue: number[] = [];

  // Seed from all edge pixels
  for (let x = 0; x < width; x++) {
    queue.push(x);
    queue.push((height - 1) * width + x);
  }
  for (let y = 1; y < height - 1; y++) {
    queue.push(y * width);
    queue.push(y * width + width - 1);
  }

  // BFS flood fill — only remove connected background regions from edges
  const result = new Uint8ClampedArray(data); // Start with original
  while (queue.length > 0) {
    const idx = queue.pop()!;
    if (idx < 0 || idx >= width * height || visited[idx]) continue;
    visited[idx] = 1;

    const pixelIdx = idx * 4;
    // Check if this pixel was marked as background
    if (output[pixelIdx + 3] < 200) {
      result[pixelIdx + 3] = output[pixelIdx + 3]; // Apply transparency

      // Expand to neighbors
      const x = idx % width;
      const y = Math.floor(idx / width);
      if (x > 0) queue.push(idx - 1);
      if (x < width - 1) queue.push(idx + 1);
      if (y > 0) queue.push(idx - width);
      if (y < height - 1) queue.push(idx + width);
    }
  }

  return new ImageData(result, width, height);
}

export interface BackgroundRemovalProps {
  /** Called when background removal is applied, with the result blob URL */
  onApply: (blobUrl: string) => void;
  className?: string;
}

/** Background removal panel */
export function BackgroundRemoval({
  onApply,
  className,
}: BackgroundRemovalProps): React.JSX.Element | null {
  const selectedArtwork = useSelectedArtwork();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const originalDataRef = useRef<ImageData | null>(null);

  const [isProcessing, setIsProcessing] = useState(false);
  const [threshold, setThreshold] = useState(30);
  const [mode, setMode] = useState<DetectionMode>('auto');
  const [hasResult, setHasResult] = useState(false);

  const imageUrl = selectedArtwork ? (getLocalImageUrl(selectedArtwork.artworkId) ?? null) : null;

  // Render background removal result onto the preview canvas (pure canvas work, no state)
  const renderRemovalPreview = useCallback((): void => {
    if (!canvasRef.current || !originalDataRef.current) return;

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    // Checkerboard pattern for transparency
    const patternCanvas = document.createElement('canvas');
    patternCanvas.width = 16;
    patternCanvas.height = 16;
    const patternCtx = patternCanvas.getContext('2d')!;
    patternCtx.fillStyle = '#e0e0e0';
    patternCtx.fillRect(0, 0, 16, 16);
    patternCtx.fillStyle = '#f5f5f5';
    patternCtx.fillRect(0, 0, 8, 8);
    patternCtx.fillRect(8, 8, 8, 8);
    const pattern = ctx.createPattern(patternCanvas, 'repeat')!;

    // Draw checkerboard background
    ctx.fillStyle = pattern;
    ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);

    // Apply background removal
    const result = removeBackground(originalDataRef.current, threshold, mode);

    // Draw result on top
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = result.width;
    tempCanvas.height = result.height;
    const tempCtx = tempCanvas.getContext('2d')!;
    tempCtx.putImageData(result, 0, 0);
    ctx.drawImage(tempCanvas, 0, 0);
  }, [threshold, mode]);

  // Load image into canvas
  const loadImage = useCallback((): void => {
    if (!imageUrl || !canvasRef.current) return;

    setIsProcessing(true);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      // Scale down for performance (max 512px)
      const maxSize = 512;
      const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      originalDataRef.current = ctx.getImageData(0, 0, canvas.width, canvas.height);

      renderRemovalPreview();
      setIsProcessing(false);
      setHasResult(true);
    };
    img.onerror = () => setIsProcessing(false);
    img.src = imageUrl;
  }, [imageUrl, renderRemovalPreview]);

  // Re-process when threshold or mode changes (canvas DOM update, no state changes)
  useEffect(() => {
    if (originalDataRef.current) {
      renderRemovalPreview();
    }
  }, [renderRemovalPreview]);

  const handleApply = (): void => {
    if (!canvasRef.current || !originalDataRef.current) return;

    // Create full-res result
    const result = removeBackground(originalDataRef.current, threshold, mode);
    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = result.width;
    outputCanvas.height = result.height;
    const ctx = outputCanvas.getContext('2d')!;
    ctx.putImageData(result, 0, 0);

    outputCanvas.toBlob((blob) => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        onApply(url);
      }
    }, 'image/png');
  };

  if (!selectedArtwork || !imageUrl) return null;

  return (
    <div className={cn('space-y-3', className)}>
      <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
        Background Removal
      </h4>

      {/* Preview canvas */}
      <div className="relative rounded-lg border overflow-hidden bg-muted/30">
        <canvas
          ref={canvasRef}
          className="w-full h-auto max-h-64 object-contain"
          style={{ display: hasResult ? 'block' : 'none' }}
        />
        {!hasResult && !isProcessing && (
          <div className="flex items-center justify-center py-8">
            <Button size="sm" onClick={loadImage}>
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14.121 14.121L19 19m-7-7l7-7m-7 7l-7 7m7-7l-7-7"
                />
              </svg>
              Remove Background
            </Button>
          </div>
        )}
        {isProcessing && (
          <div className="flex items-center justify-center py-8">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Controls */}
      {hasResult && (
        <>
          {/* Mode selector */}
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Mode</label>
            <div className="flex gap-1">
              {(
                [
                  { id: 'auto', label: 'Auto' },
                  { id: 'light', label: 'Light BG' },
                  { id: 'dark', label: 'Dark BG' },
                ] as const
              ).map((m) => (
                <button
                  key={m.id}
                  onClick={() => setMode(m.id)}
                  className={cn(
                    'flex-1 py-1 text-xs rounded border transition-colors',
                    mode === m.id
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'hover:bg-accent border-border',
                  )}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* Threshold slider */}
          <div>
            <label className="text-xs text-muted-foreground">Threshold</label>
            <Slider
              value={threshold}
              min={5}
              max={80}
              step={1}
              onChange={setThreshold}
              showValue
              formatValue={(v) => `${v}%`}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button size="sm" className="flex-1" onClick={handleApply}>
              Apply
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="flex-1"
              onClick={() => {
                setHasResult(false);
                originalDataRef.current = null;
              }}
            >
              Reset
            </Button>
          </div>

          <p className="text-[10px] text-muted-foreground">
            Tip: Adjust the threshold to fine-tune edge detection. Works best with solid-color
            backgrounds.
          </p>
        </>
      )}
    </div>
  );
}
